/** biome-ignore-all lint/a11y/noStaticElementInteractions: some divs need to be interactive */
import { useDraggable } from '@dnd-kit/core';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { KeyboardArrowDown, KeyboardArrowRight } from '@tempo-io/sui-icons';
import { Tag } from '@tempo-io/sui-tag';
import { Tooltip, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { addDays, differenceInDays, format } from 'date-fns';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { Resizable, type ResizeCallbackData } from 'react-resizable';
import { SwimlaneCustomerChip } from '@/components/common/custom-field-value/CustomerFieldDisplay';
import type { LabelWithField } from '@/components/ui/swimlane/types';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import { formatDateNumeric } from '@/lib/utils/common/dateFormats';
import { getContrastTextColor, SUBHEADER_OPACITY } from '@/lib/utils/loop-visualization/colors';
import { ITEM_TOOLTIP_DELAY } from './TimelineItemConstants';
import { TimelineItemResizeHandle } from './TimelineItemResizeHandle';
import { TimelineItemTooltip } from './TimelineItemTooltip';
import { RUBBER_BAND_ACTIVE_ATTR } from './TimelineRubberBand';
import type { TimelineItemType, TimelineRowType } from './useTimelineTableColumns';
import { darkenTimelineBgColor, lightenTimelineBgColor } from './utils/timelineColorUtils';

export {
  ITEM_HEIGHT,
  ITEM_HEIGHT_COMPACT,
  ITEM_HEIGHT_WITH_LABELS,
  ITEM_LANE_HEIGHT,
  ITEM_LANE_HEIGHT_COMPACT,
} from './TimelineItemConstants';

export const TimelineItem = memo(
  ({
    item,
    color,
    isCompact,
    calculatedWidth,
    itemTopOffset,
    itemHeight,
    progress,
    fontSize,
    timelineStartDate,
    timelineEndDate,
    startDateToUse,
    endDateToUse,
    isExpanded,
    hasChildren,
    onExpandToggle,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    isChild,
    onItemClick,
    dayWidth,
    onResizeEnd,
    row,
    hideName = false,
    isPublishedMode = false,
    showDates = true,
    timelineItemClassName,
    timelineItemTextColor = 'text-text-neutral-inverted',
    labels,
    alignNameLeft = false,
    isSelected = false,
    isInMultiSelectDrag = false,
    getSelectedItemIds,
    onSelectionClick,
  }: {
    item: TimelineItemType;
    color: string;
    isCompact: boolean;
    calculatedWidth: number;
    itemTopOffset: number;
    itemHeight: number;
    progress: number | null;
    fontSize: number;
    timelineStartDate: string;
    timelineEndDate: string;
    startDateToUse: string;
    endDateToUse: string;
    isExpanded?: boolean;
    hasChildren?: boolean;
    canResize: boolean;
    canDragBetweenHeaders: boolean;
    canDragDates: boolean;
    isChild: boolean;
    onItemClick: (itemId: number) => void;
    dayWidth: number;
    onExpandToggle?: (e: React.MouseEvent) => void;
    onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
    row?: TimelineRowType;
    hideName?: boolean;
    isPublishedMode?: boolean;
    showDates?: boolean;
    timelineItemClassName?: string;
    labels?: LabelWithField[];
    timelineItemTextColor?: string;
    alignNameLeft?: boolean;
    isSelected?: boolean;
    isInMultiSelectDrag?: boolean;
    getSelectedItemIds?: () => number[] | null;
    onSelectionClick?: (
      itemId: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
    ) => boolean;
  }) => {
    const ideaManagerEnabled = useFlag('ideaManagerEnabled');
    const hasProgress = progress !== null;
    const progressFillColor = lightenTimelineBgColor(color, SUBHEADER_OPACITY);
    const visibleLabels = labels?.filter((l) => Boolean(l?.value)) ?? [];
    const showLabels = !isCompact && visibleLabels.length > 0;
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const resetMouseXTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isDelayedHover, setIsDelayedHover] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const initialWidthRef = useRef<number>(0);
    const [lockedMouseX, setLockedMouseX] = useState(0);
    const [resizeState, setResizeState] = useState<{
      width: number;
      rightOffset: number;
      handle: 'w' | 'e';
      startDate?: string;
      endDate?: string;
    } | null>(null);

    const startDateToUseForDrag = item.plannedStartDateTime;
    const endDateToUseForDrag = item.plannedFinishDateTime;
    const duration =
      startDateToUseForDrag && endDateToUseForDrag
        ? differenceInDays(new Date(endDateToUseForDrag), new Date(startDateToUseForDrag)) + 1
        : 1;

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `timeline-item-${item.id}`,
      data: {
        type: 'timeline-item',
        itemId: item.id,
        startDate: startDateToUseForDrag,
        endDate: item.plannedFinishDateTime,
        duration,
        currentRow: row,
        order: item.order ?? 0,
        color,
        name: item.name,
        isCompact,
        selectedItemIds: isInMultiSelectDrag ? (getSelectedItemIds?.() ?? null) : null,
      },
      disabled: (!canDragBetweenHeaders && !canDragDates) || !!resizeState || isChild,
    });

    const isTooltipOpen = !!resizeState || isDelayedHover;

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      if (document.body.hasAttribute(RUBBER_BAND_ACTIVE_ATTR)) return;
      if (resetMouseXTimeoutRef.current) {
        clearTimeout(resetMouseXTimeoutRef.current);
        resetMouseXTimeoutRef.current = null;
      }
      const rect = itemRef.current?.getBoundingClientRect();
      if (rect) {
        setLockedMouseX(e.clientX - rect.left);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        if (document.body.hasAttribute(RUBBER_BAND_ACTIVE_ATTR)) return;
        setIsDelayedHover(true);
      }, ITEM_TOOLTIP_DELAY);
    };

    const handleMouseLeave = () => {
      setIsDelayedHover(false);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      resetMouseXTimeoutRef.current = setTimeout(() => setLockedMouseX(0), 150);
    };

    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        if (resetMouseXTimeoutRef.current) {
          clearTimeout(resetMouseXTimeoutRef.current);
        }
      };
    }, []);

    const resizedWidth = resizeState ? resizeState.width : calculatedWidth;

    const onResizeStart = (_e: React.SyntheticEvent, { size, handle }: ResizeCallbackData) => {
      initialWidthRef.current = calculatedWidth;
      setResizeState({
        width: size.width,
        rightOffset: 0,
        handle: handle as 'w' | 'e',
        startDate: item.plannedStartDateTime || undefined,
        endDate: item.plannedFinishDateTime || undefined,
      });
    };

    const onResize = (_e: React.SyntheticEvent, { handle, size }: ResizeCallbackData) => {
      const widthChange = size.width - initialWidthRef.current;
      const daysChange = Math.round(widthChange / dayWidth);
      if (handle === 'w') {
        const newStartDate = addDays(startDateToUse, -daysChange);
        const rightOffset = initialWidthRef.current - size.width;

        setResizeState((prev) => ({
          ...(prev ? prev : {}),
          width: size.width,
          rightOffset,
          handle: handle as 'w' | 'e',
          startDate: format(newStartDate, 'yyyy-MM-dd'),
        }));
      } else if (handle === 'e') {
        const newEndDate = addDays(endDateToUse, daysChange);

        setResizeState((prev) => ({
          ...(prev ? prev : {}),
          width: size.width,
          rightOffset: 0,
          handle: handle as 'w' | 'e',
          endDate: format(newEndDate, 'yyyy-MM-dd'),
        }));
      }
    };

    const onResizeStop = (_e: React.SyntheticEvent, { size, handle }: ResizeCallbackData) => {
      const widthChange = size.width - initialWidthRef.current;
      const daysChange = Math.round(widthChange / dayWidth);

      let newStartDate = startDateToUse;
      let newEndDate = endDateToUse;

      if (handle === 'w') {
        newStartDate = format(addDays(new Date(startDateToUse), -daysChange), 'yyyy-MM-dd');
      } else if (handle === 'e') {
        newEndDate = format(addDays(new Date(endDateToUse), daysChange), 'yyyy-MM-dd');
      }
      if (onResizeEnd) {
        onResizeEnd(item.id, newStartDate, newEndDate);
      }

      initialWidthRef.current = 0;

      setResizeState(null);
    };

    const tooltipStartDate = resizeState?.startDate || item.plannedStartDateTime;
    const tooltipEndDate = resizeState?.endDate || item.plannedFinishDateTime;

    const displayStartDate = tooltipStartDate || item.plannedStartDateTime;
    const displayEndDate = tooltipEndDate || item.plannedFinishDateTime;

    const disableResize = !canResize || isChild;
    const canDrag = (canDragBetweenHeaders || canDragDates) && !isChild;

    return (
      <div
        className={cn(
          'absolute group z-20',
          isCompact ? 'rounded-lg' : 'rounded-2xl',
          {
            'bg-bg-subtle!': !isChild && isCompact && !isDragging,
            'bg-bg-surface-base': !isChild && !isCompact && !isDragging,
          },
          timelineItemClassName,
        )}
        style={{
          top: itemTopOffset,
          left: resizeState ? resizeState.rightOffset : 0,
          width: resizedWidth,
          height: itemHeight,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Resizable
          width={resizedWidth}
          height={itemHeight}
          resizeHandles={disableResize ? [] : ['w', 'e']}
          handle={
            <TimelineItemResizeHandle
              itemTopOffset={0}
              itemHeight={itemHeight}
              isCompact={isCompact}
              barColor={color}
            />
          }
          onResizeStart={onResizeStart}
          onResize={onResize}
          onResizeStop={onResizeStop}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <Tooltip open={isTooltipOpen}>
              <TooltipTrigger>
                {/** biome-ignore lint/a11y/useKeyWithClickEvents: needs listeners */}
                <div
                  ref={(node) => {
                    itemRef.current = node;
                    setNodeRef(node);
                  }}
                  data-timeline-item-id={isChild ? undefined : item.id}
                  data-timeline-item-name={isChild ? undefined : item.name}
                  data-timeline-item-color={isChild ? undefined : color}
                  className={cn(
                    'w-[calc(100%-16px)] left-0 overflow-hidden',
                    isCompact ? 'rounded-lg' : 'rounded-2xl',
                    {
                      'rounded-l-none': startDateToUse === timelineStartDate,
                      'rounded-r-none': endDateToUse === timelineEndDate,
                      group: isChild || !canResize,
                      'ring-2 ring-border-info ring-offset-1 ring-offset-bg-surface-base':
                        isSelected,
                    },
                    timelineItemClassName,
                  )}
                  style={{
                    backgroundColor: color,
                    height: itemHeight,
                    width: resizedWidth,
                    opacity: isDragging ? 0 : 1,
                    cursor: isDragging
                      ? 'grabbing'
                      : resizeState
                        ? 'ew-resize'
                        : canDrag
                          ? 'grab'
                          : 'pointer',
                  }}
                  onClick={(e) => {
                    const handled = onSelectionClick?.(item.id, {
                      shiftKey: e.shiftKey,
                      metaKey: e.metaKey,
                      ctrlKey: e.ctrlKey,
                    });
                    if (handled) return;
                    onItemClick(item.id);
                  }}
                  {...(!isChild ? attributes : {})}
                  {...(!isChild ? listeners : {})}
                >
                  {hasProgress && (
                    <div
                      className={cn(
                        'h-full',
                        isCompact ? 'rounded-lg' : 'rounded-xl',
                        {
                          'rounded-l-none': startDateToUse === timelineStartDate,
                          'rounded-r-none': endDateToUse === timelineEndDate,
                        },
                        timelineItemClassName,
                      )}
                      style={{
                        backgroundColor: progressFillColor,
                        width: `${progress}%`,
                      }}
                    />
                  )}
                  <div
                    className={cn(
                      'absolute inset-0 z-5 overflow-hidden px-2',
                      showLabels
                        ? 'flex flex-col justify-center gap-1 py-1'
                        : 'flex items-center justify-between',
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      {showDates && (
                        <span
                          className={cn(
                            'text-[10px] font-medium shrink-0 transition-opacity group-hover:opacity-0',
                            timelineItemTextColor,
                            resizeState && 'opacity-0',
                          )}
                        >
                          {formatDateNumeric(startDateToUse)}
                        </span>
                      )}

                      {!hideName && (
                        <span
                          className={cn(
                            'px-2 truncate font-semibold flex-1 min-w-0',
                            alignNameLeft && 'text-left',
                          )}
                          style={{
                            color: getContrastTextColor(color),
                            fontSize,
                          }}
                        >
                          {item.name}
                        </span>
                      )}

                      {hasChildren && onExpandToggle && (
                        <Button
                          asChild
                          variant="ghost"
                          size="icon-sm"
                          style={{
                            color: getContrastTextColor(color),
                            backgroundColor: darkenTimelineBgColor(color, 10),
                          }}
                          className="rounded-full mr-1 max-h-max max-w-max"
                          onClick={onExpandToggle}
                        >
                          {isExpanded ? (
                            <KeyboardArrowDown className="h-5 w-5" />
                          ) : (
                            <KeyboardArrowRight className="h-5 w-5" />
                          )}
                        </Button>
                      )}

                      {showDates && (
                        <span
                          className={cn(
                            'text-[10px] font-medium shrink-0 transition-opacity group-hover:opacity-0',
                            timelineItemTextColor,
                            resizeState && 'opacity-0',
                          )}
                        >
                          {formatDateNumeric(endDateToUse)}
                        </span>
                      )}
                    </div>

                    {showLabels && (
                      <div className="flex flex-nowrap gap-1 overflow-hidden px-2">
                        {visibleLabels.map((label) => {
                          const customerDisplayValues = ideaManagerEnabled
                            ? (label.displayValues?.filter(
                                (displayValue) => displayValue.type === 'customer',
                              ) ?? [])
                            : [];

                          if (customerDisplayValues.length > 0) {
                            return (
                              <Fragment key={`${label.fieldName}:${label.value}`}>
                                {customerDisplayValues.map((customer) => (
                                  <SwimlaneCustomerChip
                                    key={customer.id}
                                    customer={customer}
                                    compact
                                  />
                                ))}
                              </Fragment>
                            );
                          }

                          return (
                            <Tag
                              key={`${label.fieldName}:${label.value}`}
                              size="sm"
                              rounded="md"
                              aria-label={`${label.fieldName}: ${label.value}`}
                              className="shrink-0 max-w-[100px] truncate border-0"
                              style={{
                                backgroundColor: lightenTimelineBgColor(color, 70),
                                color: darkenTimelineBgColor(color, 40),
                              }}
                            >
                              {label.value}
                            </Tag>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <TimelineItemTooltip
                item={item}
                resizedWidth={resizedWidth}
                isTooltipOpen={isTooltipOpen}
                isPublishedMode={isPublishedMode}
                displayStartDate={displayStartDate}
                displayEndDate={displayEndDate}
                progress={progress}
                hasProgress={hasProgress}
                mouseX={lockedMouseX}
              />
            </Tooltip>
          </div>
        </Resizable>
      </div>
    );
  },
);

TimelineItem.displayName = 'TimelineItem';

export { TimelineItemContainer } from './TimelineItemContainer';
